/**
 * Toast Notification Hook
 *
 * Centralized toast utility that enforces i18n usage and provides
 * consistent styling with brand colors throughout the application.
 *
 * Usage:
 * ```tsx
 * import { useToast } from '@/hooks/useToast';
 *
 * function MyComponent() {
 *   const toast = useToast();
 *
 *   const handleSave = async () => {
 *     toast.success('meetingTypes:messages.saveSuccess', 'Meeting type saved!');
 *   };
 *
 *   const handleDelete = async () => {
 *     const deletePromise = deleteMeetingType(id);
 *     toast.promise(deletePromise, {
 *       loading: { key: 'common:deleting', fallback: 'Deleting...' },
 *       success: { key: 'meetingTypes:messages.deleteSuccess', fallback: 'Deleted!' },
 *       error: { key: 'common:errors.deletingFailed', fallback: 'Failed to delete' },
 *     });
 *   };
 *
 *   // For API errors - auto-maps to user-friendly messages
 *   const handleApiCall = async () => {
 *     try {
 *       await apiCall();
 *     } catch (err) {
 *       toast.errorMapped(err); // Shows full-width banner for network/server errors
 *     }
 *   };
 * }
 * ```
 */

import React, { useCallback, useMemo, useRef } from 'react';
import { toast as hotToast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { mapError, shouldShowAsBanner, type MappedError } from '../utils/errorMapper';
import { ErrorBanner } from '../components/core/ErrorBanner';

// Toast options type (compatible with react-hot-toast)
type ToastOptions = {
  duration?: number;
  position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
  style?: React.CSSProperties;
  className?: string;
  icon?: React.ReactNode;
  iconTheme?: {
    primary: string;
    secondary: string;
  };
  ariaProps?: {
    role: 'status' | 'alert';
    'aria-live': 'assertive' | 'off' | 'polite';
  };
};

/**
 * Message configuration for toast notifications
 */
interface ToastMessage {
  /** Translation key (e.g., 'meetingTypes:messages.saveSuccess') */
  key: string;
  /** English fallback text */
  fallback: string;
  /** Optional interpolation values */
  values?: Record<string, string | number>;
}

/**
 * Promise toast messages configuration
 */
interface PromiseMessages {
  loading: ToastMessage;
  success: ToastMessage;
  error: ToastMessage;
}

/**
 * Brand-themed toast options with CSS variables
 * Uses the brand color system from brandColorTheme.ts
 */
const BRAND_TOAST_OPTIONS = {
  success: {
    style: {
      background: 'var(--brand-color-very-light, #eaf2f5)', // Light blue background
      color: 'var(--brand-color, #2E7AA0)', // Brand color text
      border: '1px solid var(--brand-color-light, #A3D3EB)', // Light blue border
      padding: '16px',
      fontSize: '14px',
      fontWeight: '500',
    },
    iconTheme: {
      primary: 'var(--brand-color, #2E7AA0)',
      secondary: 'var(--brand-color-very-light, #eaf2f5)',
    },
    duration: 5000,
  },
  error: {
    style: {
      background: '#FEF2F2', // red-50
      color: '#991B1B', // red-800
      border: '1px solid #FCA5A5', // red-300
      padding: '16px',
      fontSize: '14px',
      fontWeight: '500',
    },
    iconTheme: {
      primary: '#991B1B', // red-800
      secondary: '#FEF2F2', // red-50
    },
    duration: 6000, // Errors shown slightly longer
  },
  info: {
    style: {
      background: 'var(--brand-color-very-light, #eaf2f5)',
      color: 'var(--brand-color, #2E7AA0)',
      border: '1px solid var(--brand-color-light, #A3D3EB)',
      padding: '16px',
      fontSize: '14px',
      fontWeight: '500',
    },
    iconTheme: {
      primary: 'var(--brand-color, #2E7AA0)',
      secondary: 'var(--brand-color-very-light, #eaf2f5)',
    },
    duration: 5000,
  },
  loading: {
    style: {
      background: '#FFFFFF',
      color: '#374151', // gray-700
      border: '1px solid #E5E7EB', // gray-200
      padding: '16px',
      fontSize: '14px',
      fontWeight: '500',
    },
    iconTheme: {
      primary: 'var(--brand-color, #2E7AA0)',
      secondary: '#FFFFFF',
    },
  },
};

/**
 * Custom hook for toast notifications with i18n support
 *
 * Provides a centralized interface for displaying toast notifications
 * with automatic translation and brand-themed styling.
 *
 * @returns Toast notification methods
 */
export const useToast = () => {
  const { t } = useTranslation();

  // Track active error banner to prevent duplicates
  const activeBannerRef = useRef<string | null>(null);

  // Store t function in ref to avoid dependency issues
  const tRef = useRef(t);
  tRef.current = t;

  /**
   * Display a success toast notification
   *
   * @param key - Translation key (e.g., 'meetingTypes:messages.saveSuccess')
   * @param fallback - English fallback text
   * @param values - Optional interpolation values
   * @param options - Additional toast options
   */
  const success = useCallback((
    key: string,
    fallback: string,
    values?: Record<string, string | number>,
    options?: ToastOptions
  ) => {
    const message = tRef.current(key, fallback, values);
    return hotToast.success(message, {
      ...BRAND_TOAST_OPTIONS.success,
      ...options,
    });
  }, []);

  /**
   * Display an error toast notification
   *
   * @param key - Translation key (e.g., 'common:errors.savingFailed')
   * @param fallback - English fallback text
   * @param values - Optional interpolation values
   * @param options - Additional toast options
   */
  const error = useCallback((
    key: string,
    fallback: string,
    values?: Record<string, string | number>,
    options?: ToastOptions
  ) => {
    const message = tRef.current(key, fallback, values);
    return hotToast.error(message, {
      ...BRAND_TOAST_OPTIONS.error,
      ...options,
    });
  }, []);

  /**
   * Display an info toast notification
   *
   * @param key - Translation key (e.g., 'common:info.saved')
   * @param fallback - English fallback text
   * @param values - Optional interpolation values
   * @param options - Additional toast options
   */
  const info = useCallback((
    key: string,
    fallback: string,
    values?: Record<string, string | number>,
    options?: ToastOptions
  ) => {
    const message = tRef.current(key, fallback, values);
    return hotToast(message, {
      ...BRAND_TOAST_OPTIONS.info,
      ...options,
    });
  }, []);

  /**
   * Display a loading toast notification
   * Returns a toast ID that can be used with dismiss()
   *
   * @param key - Translation key (e.g., 'common:loading.saving')
   * @param fallback - English fallback text
   * @param values - Optional interpolation values
   */
  const loading = useCallback((
    key: string,
    fallback: string,
    values?: Record<string, string | number>
  ) => {
    const message = tRef.current(key, fallback, values);
    return hotToast.loading(message, BRAND_TOAST_OPTIONS.loading);
  }, []);

  /**
   * Display a promise-based toast notification
   * Automatically shows loading → success/error transition
   *
   * Provides best UX for async operations (save, delete, fetch)
   *
   * @param promise - The promise to track
   * @param messages - Messages for each state (loading, success, error)
   *
   * @example
   * ```tsx
   * toast.promise(saveMeetingType(data), {
   *   loading: { key: 'common:saving', fallback: 'Saving...' },
   *   success: { key: 'meetingTypes:messages.saveSuccess', fallback: 'Saved!' },
   *   error: { key: 'common:errors.savingFailed', fallback: 'Failed to save' },
   * });
   * ```
   */
  const promise = useCallback(<T,>(
    promiseToTrack: Promise<T>,
    messages: PromiseMessages
  ): Promise<T> => {
    return hotToast.promise(
      promiseToTrack,
      {
        loading: tRef.current(messages.loading.key, messages.loading.fallback, messages.loading.values),
        success: tRef.current(messages.success.key, messages.success.fallback, messages.success.values),
        error: tRef.current(messages.error.key, messages.error.fallback, messages.error.values),
      },
      {
        loading: BRAND_TOAST_OPTIONS.loading,
        success: BRAND_TOAST_OPTIONS.success,
        error: BRAND_TOAST_OPTIONS.error,
      }
    );
  }, []);

  /**
   * Manually dismiss a toast by ID
   * Useful for dismissing loading toasts
   *
   * @param toastId - The ID returned from loading() or other toast methods
   */
  const dismiss = useCallback((toastId?: string) => {
    hotToast.dismiss(toastId);
  }, []);

  /**
   * Dismiss all active toasts
   */
  const dismissAll = useCallback(() => {
    hotToast.dismiss();
  }, []);

  /**
   * Display a full-width error banner at the top of the screen
   * Used for prominent errors like network issues or server failures
   *
   * @param key - Translation key for the error message
   * @param fallback - English fallback text
   * @param values - Optional interpolation values
   * @param duration - Auto-dismiss duration in ms (default 3000)
   */
  const errorBanner = useCallback(
    (
      key: string,
      fallback: string,
      values?: Record<string, string | number>,
      duration?: number
    ): string => {
      // Dismiss any existing banner first
      if (activeBannerRef.current) {
        hotToast.dismiss(activeBannerRef.current);
      }

      const message = tRef.current(key, fallback, values);
      // Generate unique ID for tracking
      const toastId = `error-banner-${Date.now()}`;

      // Using type assertion as react-hot-toast types don't properly reflect
      // that custom() accepts a render function
      (hotToast.custom as unknown as (
        message: (t: { id: string; visible: boolean }) => React.ReactNode,
        opts?: object
      ) => void)(
        (toastArg) =>
          React.createElement(ErrorBanner, {
            message,
            visible: toastArg.visible,
            duration: duration || 3000,
            onDismiss: () => hotToast.dismiss(toastArg.id),
          }),
        {
          id: toastId,
          duration: (duration || 3000) + 300, // Add extra time for exit animation
          position: 'top-center',
          style: {
            background: 'transparent',
            boxShadow: 'none',
            padding: 0,
            maxWidth: '100vw',
            width: '100vw',
            margin: 0,
          },
        }
      );

      activeBannerRef.current = toastId;
      return toastId;
    },
    []
  );

  /**
   * Display an error notification with automatic error mapping
   * Maps technical errors to user-friendly messages
   *
   * - Network/server errors → Full-width error banner
   * - Other errors → Regular error toast
   *
   * @param err - The error object (axios error, Error, or any thrown value)
   *
   * @example
   * ```tsx
   * try {
   *   await saveData();
   * } catch (err) {
   *   toast.errorMapped(err);
   * }
   * ```
   */
  const errorMapped = useCallback(
    (err: unknown): string => {
      const mapped: MappedError = mapError(err);
      const message = tRef.current(mapped.translationKey, mapped.fallback);

      // Network and server errors show as prominent banners
      if (shouldShowAsBanner(err)) {
        return errorBanner(mapped.translationKey, mapped.fallback);
      }

      // Other errors show as regular toasts
      // Note: hotToast.error does return a string ID despite types suggesting void
      return hotToast.error(message, BRAND_TOAST_OPTIONS.error) as unknown as string;
    },
    [errorBanner]
  );

  /**
   * Display a "back online" success notification
   * Used by network status monitoring when connectivity is restored
   */
  const backOnline = useCallback(() => {
    return success(
      'common:messages.backOnline',
      "You're back online",
      undefined,
      { duration: 3000 }
    );
  }, [success]);

  /**
   * Display an "offline" error banner
   * Used by network status monitoring when connectivity is lost
   */
  const offline = useCallback(() => {
    return errorBanner(
      'common:errors.networkOffline',
      "You're offline. Check your connection",
      undefined,
      5000 // Show longer for offline status
    );
  }, [errorBanner]);

  // Memoize the returned object to prevent unnecessary re-renders
  // and avoid infinite loops when toast is used in useCallback dependencies
  return useMemo(() => ({
    success,
    error,
    info,
    loading,
    promise,
    dismiss,
    dismissAll,
    // New methods for error handling
    errorBanner,
    errorMapped,
    backOnline,
    offline,
  }), [success, error, info, loading, promise, dismiss, dismissAll, errorBanner, errorMapped, backOnline, offline]);
};

// Export type for use in other files
export type UseToastReturn = ReturnType<typeof useToast>;
